import type { SlideDeck } from './slideData';
import { BAND1_MONTHLY, BAND1_ANNUAL, ONBOARDING_BASE, MIN_USERS } from '../lib/pricingConstants';

const customCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

.dark-theme .reveal { font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal h1, .dark-theme .reveal h2 {
	font-family: 'DM Sans', sans-serif;
	color: #e8e8f0;
	text-transform: none;
	letter-spacing: -0.02em;
	font-weight: 700;
}
.dark-theme .reveal h1 { font-size: 2.6em; line-height: 1.1; }
.dark-theme .reveal h2 { font-size: 1.8em; line-height: 1.2; margin-bottom: 0.6em; }
.dark-theme .reveal h3 { font-size: 1.2em; line-height: 1.3; color: #E8491C; font-weight: 600; font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal p, .dark-theme .reveal li { font-size: 0.72em; line-height: 1.6; color: #f0f0f5; }
.dark-theme .reveal ul { list-style: none; padding-left: 0; }
.dark-theme .reveal ul li { padding: 0.3em 0 0.3em 1.4em; position: relative; }
.dark-theme .reveal ul li::before { content: ''; position: absolute; left: 0; top: 0.75em; width: 6px; height: 6px; border-radius: 50%; background: #E8491C; }
.dark-theme .reveal .slides section { padding: 40px 60px; text-align: left; }

.regulatory-accent { width: 60px; height: 4px; background: #E8491C; border-radius: 2px; margin-bottom: 1.2em; }

.regulatory-bq { background: rgba(255,255,255,0.04); border-left: 4px solid #E8491C; padding: 0.8em 1.2em; margin: 0.8em 0; border-radius: 0 8px 8px 0; width: 90%; box-shadow: none; }
.regulatory-bq p { color: #f0f0f5 !important; font-size: 0.78em !important; font-weight: 400; }

.regulatory-section { text-align: center !important; }
.regulatory-section-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 500; margin-bottom: 0.4em; display: block; }

.regulatory-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; font-size: 1.2em; margin-bottom: 12px; }
.regulatory-icon-orange { background: rgba(232,73,28,0.15); }
.regulatory-icon-blue { background: rgba(59,130,246,0.15); }
.regulatory-icon-green { background: rgba(34,197,94,0.15); }
.regulatory-icon-amber { background: rgba(245,158,11,0.15); }

.regulatory-card-row { display: flex; gap: 20px; margin-top: 0.8em; }
.regulatory-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; }
.regulatory-card h3 { margin-top: 0; margin-bottom: 0.3em; font-size: 1em; }
.regulatory-card .card-q { font-size: 0.65em; color: #9898b0; font-style: italic; margin-bottom: 0.6em; }
.regulatory-card p, .regulatory-card li { font-size: 0.62em; }

.regulatory-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 0.8em; }
.regulatory-price-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; text-align: center; }
.regulatory-price-card.highlight { border-color: #E8491C; background: rgba(232,73,28,0.06); }
.regulatory-price-card .label { font-size: 0.6em; color: #9898b0; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
.regulatory-price-card .amount { font-size: 1.8em; font-weight: 700; color: #e8e8f0; margin: 0.2em 0; }
.regulatory-price-card .amount .currency { font-size: 0.6em; vertical-align: top; }
.regulatory-price-card .detail { font-size: 0.55em; color: #9898b0; }

.regulatory-big-stat { font-size: 1.95em; font-weight: 700; color: #E8491C; line-height: 1; margin: 0.2em 0; }

.regulatory-flow { display: flex; align-items: center; gap: 0; margin-top: 1em; justify-content: center; }
.regulatory-flow-step { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 16px 20px; text-align: center; min-width: 140px; }
.regulatory-flow-step .name { font-size: 0.7em; font-weight: 600; color: #e8e8f0; }
.regulatory-flow-step .desc { font-size: 0.52em; color: #9898b0; margin-top: 4px; }
.regulatory-flow-arrow { font-size: 1.2em; color: #E8491C; padding: 0 8px; }

.regulatory-demo-badge { display: inline-block; background: #E8491C; color: white; font-size: 0.5em; font-weight: 600; padding: 4px 14px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.6em; }

.regulatory-cta { background: linear-gradient(135deg, rgba(232,73,28,0.1), rgba(232,73,28,0.03)); border: 1px solid rgba(232,73,28,0.2); border-radius: 16px; padding: 32px 40px; margin-top: 1em; }
.regulatory-cta p { color: #f0f0f5 !important; font-size: 0.78em !important; }

.regulatory-compact li { padding: 0.15em 0 0.15em 1.4em !important; }
.regulatory-compact li::before { top: 0.55em !important; }

.our-source-grid { display: flex; gap: 16px; margin-top: 0.8em; }
.our-source-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 20px; }
.our-source-card .src-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 600; }
.our-source-card h3 { margin: 0.3em 0; font-size: 0.92em; }
.our-source-card p { font-size: 0.6em !important; color: #c8c8d4; }

/* Hurricane Melissa — full-slide satellite background; scrim keeps text legible */
.melissa-stage { position: relative; z-index: 2; }
.melissa-stage h2,
.melissa-stage p,
.melissa-stage li { text-shadow: 0 1px 14px rgba(0,0,0,0.8), 0 0 3px rgba(0,0,0,0.65); }

/* Livewire side-logo (bottom-left): render white and a touch larger over the dark deck */
#side { width: 150px !important; }
#side svg { filter: brightness(0) invert(1); }
`;

// Derived pricing for slides (5-8 analysts, monthly, no annual discount)
const saasLow = (5 * BAND1_MONTHLY * 12).toLocaleString();
const saasHigh = (8 * BAND1_MONTHLY * 12).toLocaleString();
const yearOneLow = (5 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const yearOneHigh = (8 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const onboardingDisplay = ONBOARDING_BASE.toLocaleString();
const minMonthly = (MIN_USERS * BAND1_MONTHLY).toLocaleString();

export const ourPresentation: SlideDeck = {
	slug: 'livewire-office-of-utility-regulations-presentation',
	name: 'OUR Presentation',
	subtitle: 'Independent regulatory oversight, prepared for the OUR',
	color: '#1a1a2e',
	darkTheme: true,
	customCSS,
	slides: [
		// 1 — Title
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p><i>[Let it sit a beat before you speak.]</i></p><p>"Thank you for the time. I'm Rhys Stewart, from Auchindown. What I want to walk you through today is a platform we've built called Livewire — and specifically, what it looks like when a regulator has its own independent window into the network it oversees. I'll keep it concrete, and I'd rather this be a conversation than a monologue, so stop me wherever you like."</p>`,
			content: `
				<h1 style="font-size:3em;margin-bottom:0.1em;font-family:lvwr-ubuntu-thin,sans-serif;">
					<span style="color:#E8491C;font-weight:700;">Livewire</span>
				</h1>
				<p style="font-size:1.1em;color:#f0f0f5;font-weight:400;margin-top:0.3em;margin-bottom:0.6em;">
					Enabling Independent Regulatory Oversight
				</p>
				<div class="fragment">
					<p style="font-size:0.6em;color:#9898b0;font-weight:500;letter-spacing:0.05em;text-transform:uppercase;">
					Prepared for the Office of Utilities Regulation
					</p>
					<img src="/our-logo-white-trim.png" alt="Office of Utilities Regulation" style="height:46px;width:auto;opacity:0.82;margin-top:0.8em;margin-bottom:0.4em;display:block;" />
				</div>
				<div  class="fragment" style="font-size:0.6em;color:#f0f0f5;border-top:1px solid rgba(255,255,255,0.08);padding-top:1em;margin-top:1.5em;">
					<br>Rhys Stewart <br> Auchindown<br>
				</div>`,
		},
		// 2 — Section 01
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Let me start not with the product, but with the problem it exists to solve. And it's a structural one — it's about information."</p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">01</span>
					<h2 style="font-size:2.2em;">The Challenge</h2>
					<p style="max-width:600px;margin:0 auto;">Information asymmetry in regulatory oversight</p>
				</div>`,
		},
		// 3 — The problem
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"The OUR makes consequential decisions."</p><p><i>[advance]</i> "Rate cases. Performance evaluations. Disaster preparedness. Oversight of where infrastructure investment goes."</p><p><i>[advance]</i> "And almost all of the data informing those decisions flows through the very entity being regulated. In Jamaica, that means JPS."</p><p><i>[advance]</i> "I want to be careful here — that's not a criticism of JPS. It's a structural reality of how utility regulation works everywhere. The regulator depends on the regulated, not just for the grid data, but for the <b>analysis</b> of it."</p><p><i>[advance]</i> "And what tends to arrive are summaries and reports. The underlying methods, the calculations behind them — those usually stay a black box."</p><p>"So the question I'd pose is: how much of the OUR's picture of the network is genuinely the OUR's own?"</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>The OUR makes crucial decisions, often with limited independent visibility</h2>
				<ul>
					<li class="fragment">Rate cases, performance evaluations, disaster preparedness, infrastructure investment oversight</li>
					<li class="fragment">These decisions are informed almost entirely by data that flows through the entity being regulated. In Jamaica, that means JPS</li>
					<li class="fragment">Not a criticism, simply a structural reality. The regulator is dependent on the regulated to share not only grid information, but also the analysis of it</li>
					<li class="fragment">What's provided are summaries and reports. The underlying methods and calculations are usually a black box</li>
				</ul>`,
		},
		// 4 — Hurricane Melissa (explicit)
		{
			title: '',
			background: '#1a1a2e',
			backgroundVideo: '/hurricane-melissa-bg.webm',
			notes: `<p><i>[Heavy slide. Slow down. Let the background carry weight.]</i></p><p>"Melissa made this abstract problem very concrete."</p><p><i>[advance]</i> "In the aftermath, the OUR needed to understand things like —"</p><p><i>[advance]</i> "the scope and extent of damage across the network;"</p><p><i>[advance]</i> "how fast restoration was actually progressing, parish by parish;"</p><p><i>[advance]</i> "and the ongoing condition of the network as crews worked."</p><p><i>[advance — the quote]</i> "And the honest question is: how much of that picture depended on JPS telling you what was happening on the ground?"</p><p><i>[advance — the orange line. The turn of the whole deck. Say it plainly.]</i> "What if, instead, the OUR had its own independent window into the network — before, during, and after the storm?"</p><p>"That's what the rest of this is about."</p>`,
			content: `
				<div class="melissa-stage">
					<div class="regulatory-accent"></div>
					<h2>Hurricane Melissa showed us the gap</h2>
					<p style="font-size:0.85em;max-width:720px;" class="fragment">
						In the aftermath, the OUR needed to understand:
					</p>
					<ul>
						<li class="fragment">Scope and extent of damage across the network</li>
						<li class="fragment">Progress and speed of restoration, parish by parish</li>
						<li class="fragment">Ongoing network condition as crews worked</li>
					</ul>

					<div class="regulatory-bq fragment">
						<p>How much of that picture depended on JPS telling you what was happening on the ground?</p>
					</div>
					<p class="fragment" style="margin-top:1.2em;font-size:0.85em;color:#E8491C;font-weight:600;">
						What if the OUR had its own independent window into the network, before, during, and after the storm?
					</p>
				</div>`,
		},
		// 5 — Section 02
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"So — what is it. In one line: a spatial digital twin, built for regulatory intelligence."</p><p><i>[Credibility beat — say this here, it's your strongest asset and it's not on a slide:]</i> "And before I go further — I'm not coming at this from the outside. I spent close to two decades inside JPS as a GIS analyst, doing exactly this kind of network data work. Livewire is, in a sense, the tool I always wished the regulator had on the other side of the table."</p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">02</span>
					<h2 style="font-size:2.2em;">What is <span style="font-family:lvwr-ubuntu-thin,sans-serif;color:#E8491C;">Livewire</span>?</h2>
					<p style="max-width:600px;margin:0 auto;">A spatial digital twin for regulatory intelligence</p>
				</div>`,
		},
		// 6 — Digital twin
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"At its core, Livewire is a platform for three things —"</p><p><i>[advance]</i> "seeing, tracking, and analyzing the state of the network, <b>independently</b>."</p><p><i>[advance]</i> "Now, an important boundary: this is not an OMS, and it's not an ADMS. It does not replace or compete with the tools JPS uses to <b>run</b> the grid. Different job entirely."</p><p><i>[advance]</i> "It's browser-based. No GIS software, no desktop installation, nothing to set up."</p><p><i>[advance]</i> "And it's mobile-ready — your staff can inspect an asset and record what they find from a phone or tablet, standing in front of it."</p><p><i>[advance — the quote]</i> "JPS has tools for <b>operating</b> the network. Livewire gives the OUR a tool for <b>overseeing</b> it."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>A cloud-based spatial digital twin</h2>
				<ul>
					<li class="fragment">A platform for <strong style="color:#e8e8f0;">seeing, tracking, and analyzing</strong> network state <u>independently</u></li>
					<li class="fragment">Not an OMS or an ADMS. It does not replace or compete with the utility's operational tools</li>
					<li class="fragment">Browser-based, with no GIS software or desktop installation</li>
					<li class="fragment">Mobile ready. Inspect assets and annotate findings directly from a phone or tablet in the field</li>
				</ul>
				<div class="regulatory-bq fragment" style="margin-top:1em;">
					<p>JPS has tools for <em>operating</em> the network.<br>
					Livewire gives the OUR a tool for <em>overseeing</em> it.</p>
				</div>`,
		},
		// 7 — Data provenance (NEW) — "Where does the data come from?"
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p><i>[The objection slide. Invite it — don't dodge it.]</i></p><p>"The first question any technical person asks me here is the right one: <b>where does the data actually come from?</b> Three sources."</p><p><i>[advance]</i> "One — utility submissions. The OUR already receives data from JPS through the regulatory framework, and you're entitled to more. Livewire takes whatever lands and makes it structured and queryable — and lets the OUR specify the format it arrives in, rather than accepting a PDF summary."</p><p><i>[advance]</i> "Two — the OUR's own field collection. Your staff inspecting assets and recording findings directly. Independent ground truth, captured by you."</p><p><i>[advance]</i> "Three — open and public sources. Imagery, parish and administrative boundaries, publicly available infrastructure data."</p><p><i>[advance — the key line of the whole pitch]</i> "Independence was never about excluding JPS's data — you need it, and you're entitled to it. Independence is about what <b>happens</b> to that data once it's yours. Once it lives in the OUR's own versioned, timestamped, queryable system, you can verify it, trace its history, and check it against what's actually on the ground."</p><p><i>If pressed — "but if it's still JPS's submitted data, how is it independent?":</i> "Two ways. First, the OUR sets the terms — once the data lands in a structured, granular model rather than a PDF, the format stops being a black box. Second, verification doesn't come <b>from</b> the submission — it comes from checking it against your field findings, its change history, the baseline. The submission is the starting point, not the final word."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>"But where does the data come from?"</h2>
				<div class="our-source-grid">
					<div class="our-source-card fragment">
						<span class="src-num">01</span>
						<h3>Utility submissions</h3>
						<p>Whatever the OUR receives from JPS under the regulatory framework, Livewire structures into a queryable model. And the OUR sets the format it arrives in.</p>
					</div>
					<div class="our-source-card fragment">
						<span class="src-num">02</span>
						<h3>The OUR's own field collection</h3>
						<p>Staff inspect assets and record findings directly. Independent ground truth, captured in the field.</p>
					</div>
					<div class="our-source-card fragment">
						<span class="src-num">03</span>
						<h3>Open &amp; public sources</h3>
						<p>Imagery, parish and administrative boundaries, and publicly available infrastructure data.</p>
					</div>
				</div>
				<div class="regulatory-bq fragment" style="margin-top:1em;">
					<p>Independence isn't about excluding JPS's data. It's about what happens to it. Once it lives in the OUR's own versioned, timestamped, queryable system, you can verify it, trace its history, and check it against the ground.</p>
				</div>`,
		},
		// 8 — Section 03
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"So that's the foundation. Let me show you what you actually <b>do</b> with it. Six capabilities — and they aren't six separate features, they form a single loop. I'll come back to that."</p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">03</span>
					<h2 style="font-size:2.2em;">The Regulator's Toolkit</h2>
					<p style="max-width:600px;margin:0 auto;">Six capabilities. One oversight loop.</p>
				</div>`,
		},
		// 9 — Spotlight
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Spotlight answers 'what's there?' You draw a boundary around any area — a parish, a feeder zone, a single community — and instantly see everything inside it."</p><p><i>[advance]</i> "Asset counts, types, conditions, how it's all connected — in seconds."</p><p><i>[advance]</i> "No request to JPS. No waiting on a report."</p><p><i>[advance]</i> "For you: consumer complaints, checking service equity across parishes, any ad-hoc investigation you need to run quickly."</p>`,
			content: `
				<div class="regulatory-icon regulatory-icon-orange">🔍</div>
				<h3>Spotlight — "What's there?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Draw a boundary around any area (a parish, a feeder zone, a community) and instantly see what's inside.
				</p>
				<ul>
					<li class="fragment">Asset counts, types, conditions, connectivity, all in seconds</li>
					<li class="fragment">No request to JPS. No waiting for a report.</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Consumer complaints, service equity across parishes, ad-hoc investigations
				</p>`,
		},
		// 10 — Tracing
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Tracing answers 'what's connected?' Pick any point on the network and trace it — upstream to the source, or downstream to every customer it serves, by phase."</p><p><i>[advance]</i> "How many customers sit downstream of an aging switch?"</p><p><i>[advance]</i> "Is this feeder balanced across phases, or is the load piled on one?"</p><p><i>[advance]</i> "What's the full path from a substation to one specific transformer?"</p><p><i>[advance]</i> "For you: the real customer impact of a failure, checking phase-balance claims, understanding where the network is exposed."</p>`,
			content: `
				<div class="regulatory-icon" style="background:rgba(139,92,246,0.15);">⚡</div>
				<h3>Tracing — "What's connected?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Select any point on the network and trace upstream to the source or downstream to every connected customer, by phase.
				</p>
				<ul>
					<li class="fragment">How many customers are downstream of an aging switch or conductor?</li>
					<li class="fragment">Is this feeder balanced across phases, or is load concentrated on one?</li>
					<li class="fragment">What's the full path from substation to a specific transformer?</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Assessing customer impact of infrastructure failures, verifying phase balance claims, understanding network exposure
				</p>`,
		},
		// 11 — Chronicle
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Chronicle answers 'what changed?' It tracks every change to the network over time, and lets you compare any two points in its history."</p><p><i>[advance]</i> "You can verify that an upgrade actually happened — independently."</p><p><i>[advance]</i> "Or set a pre-storm baseline against post-storm reality. Spatial evidence, not a damage report."</p><p><i>[advance]</i> "For you: rate-case verification, post-disaster assessment, tracking whether investment dollars produced what was promised."</p>`,
			content: `
				<div class="regulatory-icon regulatory-icon-blue">📋</div>
				<h3>Chronicle — "What changed?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Track every change to the network over time. Compare any two points in history.
				</p>
				<ul>
					<li class="fragment">Verify infrastructure upgrades independently</li>
					<li class="fragment">Pre-storm baseline vs. post-storm reality: spatial evidence, not damage reports</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Rate case verification, post-disaster assessment, infrastructure investment tracking
				</p>`,
		},
		// 12 — Annotations
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Annotations answers 'what did we find?' Send staff to the field, inspect assets, record findings directly in Livewire."</p><p><i>[advance]</i> "Flag discrepancies — missing equipment, records that don't match, conditions nobody reported."</p><p><i>[advance]</i> "Photos, comments, geolocated and timestamped, tied to the specific asset."</p><p><i>[advance]</i> "For you: field verification, compliance audits, hard evidence for a rate-case review."</p>`,
			content: `
				<div class="regulatory-icon regulatory-icon-green">📌</div>
				<h3>Annotations — "What did we find?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Send staff to the field. Inspect assets. Record findings directly in Livewire.
				</p>
				<ul>
					<li class="fragment">Flag discrepancies: missing equipment, mismatched records, unreported conditions</li>
					<li class="fragment">Photos, comments, geolocated and timestamped, tied to the specific asset</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Field verification, compliance audits, independent evidence for rate case reviews
				</p>`,
		},
		// 13 — Reliability Metrics (enhanced with JPS licence standards)
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Reliability answers 'how is JPS performing?' Livewire calculates SAIDI, SAIFI, and CAIDI <b>independently</b> — derived from the network model, not lifted from a utility-submitted report."</p><p><i>[advance]</i> "The indices come straight from the topology and the outage data."</p><p><i>[advance]</i> "Break it down by feeder, by parish, by any boundary you draw."</p><p><i>[advance]</i> "And compare JPS's reported numbers against the Guaranteed and Overall Standards written into its own licence."</p><p><i>[advance]</i> "For you: enforcing service standards, benchmarking performance, rate-case evidence, and finding the areas that are quietly underserved."</p>`,
			content: `
				<div class="regulatory-icon" style="background:rgba(168,85,247,0.15);">📊</div>
				<h3>Reliability Metrics — "How is JPS performing?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Calculate SAIDI, SAIFI, and CAIDI independently, from the network model, not from utility-submitted reports.
				</p>
				<ul>
					<li class="fragment">Derive reliability indices directly from network topology and outage data</li>
					<li class="fragment">Break down performance by feeder, parish, or any spatial boundary</li>
					<li class="fragment">Compare JPS's reported metrics against the Guaranteed and Overall Standards in its licence</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Enforcing service standards, performance benchmarking, rate case evidence, identifying underserved areas
				</p>`,
		},
		// 14 — Network Baseline
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"And underneath all of it sits the Baseline — the OUR's own record of network state. Independent, timestamped, versioned, queryable."</p><p><i>[advance — the quote]</i> "So when there's a dispute about network condition, or whether an investment was made — you're no longer comparing <b>claims</b>. You're comparing <b>data</b>."</p>`,
			content: `
				<div class="regulatory-icon regulatory-icon-amber">🏗️</div>
				<h3>Network Baseline — "What's the truth?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					The OUR's own record of network state: independent, timestamped, versioned, and queryable.
				</p>
				<div class="regulatory-bq fragment">
					<p>When there's a dispute about network condition or investment, you're not comparing claims. You're comparing data.</p>
				</div>`,
		},
		// 15 — Oversight loop
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"And here's how the six come together. Spotlight tells you what should be there. Tracing, what's connected. Chronicle, what changed. Annotations, what's actually on the ground. Reliability, how they're performing. And all of it resolves into one verified baseline — the truth, held by you."</p><p>"Independent. Continuous. And yours."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>The oversight loop</h2>
				<div class="regulatory-flow" style="justify-content:center;gap:4px;">
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Spotlight</div>
						<div class="desc">What should<br>be there?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Tracing</div>
						<div class="desc">What's<br>connected?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Chronicle</div>
						<div class="desc">What<br>changed?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Annotations</div>
						<div class="desc">What's on<br>the ground?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Reliability</div>
						<div class="desc">How are they<br>performing?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;border-color:#E8491C;">
						<div class="name" style="color:#E8491C;">Baseline</div>
						<div class="desc">The verified<br>truth</div>
					</div>
				</div>
				<p style="text-align:center;margin-top:1.2em;font-size:0.72em;">
					Independent. Continuous. Yours.
				</p>`,
		},
		// 16 — Data governance (NEW)
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"A word on the part I know you'll care about most — governance and security, because this is critical-infrastructure data."</p><p><i>[advance]</i> "The OUR gets a secure space of its own — isolated, never shared with the utility or any other tenant."</p><p><i>[advance]</i> "Role-based access, so you control exactly who inside the OUR can see and edit what."</p><p><i>[advance]</i> "Cloud-hosted and encrypted — and crucially, no servers and no GIS infrastructure for the OUR to stand up or maintain."</p><p><i>[advance]</i> "Critical-infrastructure data handled with the security it warrants."</p><p><i>[advance — the quote]</i> "Because independence ultimately depends on one thing: the regulator holding the data. With Livewire, the OUR does."</p><p><i>If asked "where is the data hosted / is it offshore?":</i> "Fair question. We can host in the jurisdiction the OUR requires — including in-country — encrypted in transit and at rest, full access logging, and a clear continuity plan so the OUR is never locked out. I'd rather agree the hosting terms with your team than dictate them from a slide."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>Your data. Your secure space. Your control.</h2>
				<ul>
					<li class="fragment"><strong style="color:#e8e8f0;">A secure space dedicated to the OUR</strong>, isolated and never shared with the utility or other tenants</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Role-based access</strong>, so you control exactly who within the OUR sees and edits what</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Cloud-hosted and encrypted</strong>, with no servers or GIS infrastructure for the OUR to maintain</li>
					<li class="fragment"><strong style="color:#e8e8f0;">Critical-infrastructure data</strong> handled with the security it warrants</li>
				</ul>
				<div class="regulatory-bq fragment" style="margin-top:1em;">
					<p>The independence of oversight depends on the regulator holding the data. With Livewire, the OUR does.</p>
				</div>`,
		},
		// 17 — Section 04 Demo (framed as JPS stand-in)
		{
			title: '',
			background: '#0f0f1e',
			notes: `<p>"Enough slides. Let me show you the real thing. This is a demonstration network I've built called Everglades Electric — completely fictional, so nothing here is anyone's real data."</p><p><i>[advance]</i> "Think of it as a stand-in for today. In production, this would be the JPS network — living in the OUR's own secure space."</p><p><i>[Switch to live demo. Lead with Spotlight (most visual), then Tracing, then Chronicle's before/after. Keep it to the three that land hardest.]</i></p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">04</span>
					<div class="regulatory-demo-badge">Live Demo</div>
					<h2 style="font-size:2.2em;">Everglades Electric</h2>
					<p style="max-width:640px;margin:0 auto;">A demonstration utility network: see what regulatory oversight looks like in Livewire</p>
					<p class="fragment" style="max-width:640px;margin:0.8em auto 0;font-size:0.62em;color:#9898b0;">
						A stand-in for today. In production, this is the JPS network, in the OUR's own secure space.
					</p>
				</div>`,
		},
		// 18 — Section 05 The Ask / Pilot (softened for first contact)
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p><i>[Soft. You're not closing — you're lowering the cost of saying yes.]</i></p><p>"So where could this go from here? I'm not going to ask you to commit to anything today."</p><p>"What I'd suggest is we explore it together with a small pilot. A chance to see what independent oversight actually looks like — using your own data, not a demo. We'd stand up a slice of the network in your own secure space, for your team to try hands-on. And then you decide whether it changes how the OUR sees the network — entirely on your terms."</p>`,
			content: `
				<div class="regulatory-section" style="text-align:left !important;">
					<span class="regulatory-section-num" style="text-align:left;">05</span>
					<h2>Proposed next steps</h2>
					<div class="regulatory-cta">
						<p><strong style="color:#E8491C;font-size:1.05em;">Let's explore it together with a small pilot.</strong></p>
						<p style="margin-top:0.6em;">A chance to see what independent oversight actually looks like, using your own data.</p>
						<p style="margin-top:0.6em;">We set up a slice of the network in your own secure space, for your team to try hands-on.</p>
						<p style="margin-top:0.6em;">You decide whether it changes how the OUR sees the network, entirely on your terms.</p>
					</div>
				</div>`,
			verticalSlides: [
				{
					title: '',
					background: '#1a1a2e',
					notes: `<p><i>[Only go here if they ask about cost. The numbers are on screen; don't read them aloud.]</i></p><p>"On cost — it's straightforward and per-user. A monthly rate, and a lower annual rate for larger teams. No GIS licensing to buy, browser-based, storage included. Happy to shape this to however the OUR actually procures software."</p>`,
					content: `
						<div class="regulatory-accent"></div>
						<h2>Simple, accessible pricing</h2>
							<div class="regulatory-card" style="text-align:center;border-color:rgba(232,73,28,0.3);background:rgba(232,73,28,0.06);">
								<p class="card-q">Starting price</p>
								<div class="regulatory-big-stat" style="font-size:3.2em;">$${minMonthly}<span style="font-size:0.28em;color:#9898b0;font-weight:600;"> / month</span></div>
								<p>Minimum ${MIN_USERS} users at $${BAND1_MONTHLY}/user, then scale up</p>
							</div>
							<div class="regulatory-card-row" style="margin-top:16px;">
								<div class="regulatory-card" style="text-align:center;">
									<p class="card-q">Monthly</p>
									<div class="regulatory-big-stat">$${BAND1_MONTHLY}</div>
									<p>per user / month</p>
								</div>
								<div class="regulatory-card" style="text-align:center;">
									<p class="card-q">Annual commitment</p>
									<div class="regulatory-big-stat">$${BAND1_ANNUAL}</div>
									<p>per user / month · 10+ users</p>
								</div>
							</div>
							<p style="margin-top:0.9em;font-size:0.62em;text-align:center;">
								100 GB storage included · No GIS licensing required · Browser-based
							</p>`,
				},
				{
					title: '',
					background: '#1a1a2e',
					notes: `<p>"To put a real number on it — for a team of five to eight analysts, you're looking at the annual figure on screen, plus a one-time onboarding to get you live in about two weeks. All-in for year one lands below what a single consultant engagement typically costs — except this doesn't end when the consultant leaves. It's a permanent oversight capability you keep."</p>`,
					content: `
						<div class="regulatory-accent"></div>
						<h2>For 5 to 8 analysts</h2>
						<div class="regulatory-card-row">
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">SaaS</p>
								<div class="regulatory-big-stat">$${saasLow} – $${saasHigh}</div>
								<p>per year</p>
							</div>
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">Onboarding (one-time)</p>
								<div class="regulatory-big-stat">$${onboardingDisplay}+</div>
								<p>Jumpstart: 2 weeks to go-live</p>
							</div>
						</div>
						<div class="regulatory-card" style="margin-top:16px;text-align:center;border-color:rgba(232,73,28,0.2);">
							<p class="card-q">Year 1 all-in</p>
							<div class="regulatory-big-stat">$${yearOneLow} – $${yearOneHigh}</div>
							<p>Less than a single consultant engagement. Permanent, continuous oversight capability</p>
						</div>`,
				},
			],
		},
		// 19 — Closing
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"That's Livewire — built to do one thing: give the regulator an independent, continuous view of the network it's responsible for. I'd genuinely value the OUR's perspective on it, and I'd love to talk about what a pilot might look like. Thank you."</p><p><i>[Stop. Let them respond. Don't fill the silence.]</i></p><hr><p><b>Q&amp;A — rehearse cold</b></p><p><b>Is JPS required to give you network data?</b> You'd know your licence conditions far better than I would — that's your domain. My point is narrower: whatever you're entitled to receive, and whatever you collect yourselves, you should be able to hold and verify in your own space. <i>(Don't assert a specific mandate — deferring reads as credibility.)</i></p><p><b>Isn't this still just JPS's data, so how is it independent?</b> Independence isn't about where the data starts — it's about what you can do with it once it's yours. The OUR sets the submission format, so it stops being a black box; and you verify it against your own field findings, its change history, and the baseline.</p><p><b>Where is the data hosted? Is it offshore?</b> We host in the jurisdiction the OUR requires, including in-country. Encrypted in transit and at rest, full access logging, continuity plan so you're never locked out.</p><p><b>How is this not duplicating what JPS already has?</b> JPS's tools operate the grid. This oversees it. Different purpose, different owner. Not a parallel control room — just the OUR's own verified picture.</p><p><b>Has any other regulator done this?</b> Not yet — which is the opportunity. The OUR could be the first, and Jamaica could set the template the rest of the region follows. CARILEC-wide, a standard waiting to happen.</p><p><b>Who builds the network model initially?</b> We do, as part of onboarding — structuring the OUR's existing submissions and public data into the model. That's the two-week jumpstart. Your team isn't doing data entry.</p><p><b>What if Auchindown disappears?</b> Your data is yours, exportable, in your own space, with a documented continuity plan. You're never holding nothing.</p>`,
			content: `
				<div class="regulatory-section">
					<h1 style="margin-bottom:0.2em;font-size:3em;font-family:lvwr-ubuntu-thin,sans-serif;">
						<span style="color:#E8491C;">Livewire</span>
					</h1>
					<p style="font-size:0.85em;color:#9898b0;margin-bottom:1.5em;">Enabling independent regulatory oversight</p>
					<p style="font-size:0.7em;color:#e8e8f0;">
						Rhys Stewart<br>
						<span style="color:#9898b0;">rhys@auchindown.com · lvwr.io</span>
					</p>
				</div>`,
		},
	],
};
